import { useEffect, useRef, useState } from 'react'

/* The casino list's tab strip + card grid, shared by Our Casino, Premium Casino
 * and VIP Casino (user, 2026-09-22: "sab same hai to shareable component bana
 * de"). Markup and classes are the reference's and were matched page by page
 * before this existed — nothing here changes them:
 *
 *   .casino-tabs-admin.p-2 > .casino-tabs-menu.w-100 >
 *       a.arrow-tabs.arrow-left + ul.nav.nav-tabs.nav-tabs-custom + a.arrow-tabs.arrow-right
 *   .casino-banners > .casino-banner-item*
 *
 * Three behaviours, all read off the reference (phone recordings, 2026-09-22,
 * Premium Casino):
 *
 * 1. ARROWS jump the strip by 300px, no JS smoothing — the reference's
 *    scroll_left/right is `.nav-tabs`.scrollLeft ∓= 300 (admin-fixes plan §25,
 *    2026-09-19). Our Casino already did this; Premium and VIP still had a
 *    200px smooth scrollBy, so the arrows reached the last tab in a different
 *    number of clicks on each page.
 *
 * 2. TAPPING A TAB slides the strip so the tab sits in the middle. Reference
 *    (capture tenexch.com-admin-casino-list-1790051668083, inline bundle,
 *    `changeGameId`): no scroll code — the tab is a real
 *    <a href="javascript:void(0)">, a click focuses it, and the browser scrolls
 *    a focused element inside the `overflow: hidden` strip into view (centred
 *    on WebKit) with the strip's `scroll-behavior: smooth`. Ours cannot lean on
 *    that: React refuses `javascript:` hrefs (it rewrites them to a throw), so
 *    the link never takes focus and the strip stayed put.
 *
 *    So the glide is done by hand, and it has to be timed BY HAND too: on iOS
 *    Safari `scrollIntoView`/`scrollTo` with smooth behaviour ran the whole
 *    move in 1-3 frames (28-40px jumps — measured on the user's recording,
 *    2026-09-22), i.e. the jerk. The reference's own glide, measured the same
 *    way (per-frame strip shift at 60fps, 5 taps): ~17 frames ≈ 280ms, an
 *    ease-in-out — 2 → 8 → 2 px per frame — every time. `glideTo()` below
 *    animates scrollLeft over TAB_GLIDE_MS with that curve, so the strip moves
 *    the same on every browser. Recording at 440px: All Casino / Roulette /
 *    Teenpatti hardly move (scrollLeft is near 0), Poker → middle, Baccarat,
 *    Dragon Tiger → middle.
 *
 *    The glide starts AFTER the blank beat below has emptied the grid, never
 *    on the same frame: emptying the grid shrinks the page, and a scroll that
 *    starts on that layout shift stutters.
 *
 * 3. THE CARDS re-enter on EVERY tab change, after a BLANK BEAT: tap a tab
 *    and the grid is empty — plain white, no spinner — for about 0.6 s, then
 *    the cards grow in (scale 0 → 1, ~0.45 s: the `slide-down` keyframe
 *    styles/style.css puts on .casino-banner-item, which only plays when the
 *    element is MOUNTED). Measured on the reference side-by-side recording
 *    (2026-09-22, 25 fps, per-frame brightness of the grid area): 15 white
 *    frames, then 11 frames of growth. Ours used to swap the cards in place —
 *    same React keys across tabs, so a card kept in the new tab was reused
 *    and never animated, and there was no gap at all; the effect showed only
 *    when a card happened to be new to that tab ("kabhi kabhi aata hai") and
 *    even then with no beat before it ("same feel nahi"). Now: on a tab
 *    change the grid unmounts, TAB_SWITCH_BLANK_MS later it mounts again
 *    under a fresh key, and the CSS entrance plays on every card.
 *
 * `renderItem(item)` is the page's — each list links to its own route and
 * image base. `items` is the page's already-filtered list for `activeTab`.
 */

const ARROW_STEP_PX = 300
// one tab-to-tab glide of the strip (reference: ~17 frames at 60fps)
const TAB_GLIDE_MS = 280
const easeInOut = (t) => (t < 0.5 ? 2 * t * t : 1 - ((-2 * t + 2) ** 2) / 2)
// the empty beat between a tab tap and the cards growing back (reference: ~15
// frames at 25 fps)
const TAB_SWITCH_BLANK_MS = 600

export default function CasinoTabsList({ tabs, activeTab, onTabChange, items, renderItem, itemKey = (it) => it.id }) {
  const tabsRef = useRef(null)
  const linkRefs = useRef({})
  const glideRef = useRef(0)

  // scrollLeft → target over TAB_GLIDE_MS, ease-in-out, on our own clock —
  // see note 2. A new glide cancels the one in flight.
  const glideTo = (target) => {
    const el = tabsRef.current
    if (!el) return
    cancelAnimationFrame(glideRef.current)
    const from = el.scrollLeft
    const max = el.scrollWidth - el.clientWidth
    const to = Math.max(0, Math.min(max, target))
    if (Math.abs(to - from) < 1) return
    const t0 = performance.now()
    const step = (now) => {
      const t = Math.min(1, (now - t0) / TAB_GLIDE_MS)
      el.scrollLeft = from + (to - from) * easeInOut(t)
      if (t < 1) glideRef.current = requestAnimationFrame(step)
    }
    glideRef.current = requestAnimationFrame(step)
  }
  useEffect(() => () => cancelAnimationFrame(glideRef.current), [])
  // on every tab change: grid gone → blank beat → grid back under a new key,
  // so the entrance plays again on every card
  const [listKey, setListKey] = useState(0)
  const [blank, setBlank] = useState(false)
  const beatRef = useRef(0)
  useEffect(() => () => clearTimeout(beatRef.current), [])

  /* Driven from the CLICK, not from an effect on `activeTab`. An earlier cut
     watched `activeTab` in an effect and cancelled its rAF in the cleanup —
     under React's dev StrictMode (mount → cleanup → mount) the cleanup ran
     right after the first mount and killed the scheduled glide, and the
     "already handled this tab" ref made the second mount a no-op: the strip
     never moved at all ("scroll hi band ho gaya"). A click handler runs once. */
  const pickTab = (tab) => {
    if (tab === activeTab) { onTabChange(tab); return }
    onTabChange(tab)
    // grid gone for the blank beat, then back under a fresh key (note 3)
    setBlank(true)
    clearTimeout(beatRef.current)
    beatRef.current = setTimeout(() => {
      setListKey((k) => k + 1)
      setBlank(false)
    }, TAB_SWITCH_BLANK_MS)
    // next frame — after the grid has gone — glide the tab to the middle
    requestAnimationFrame(() => {
      const el = tabsRef.current
      const link = linkRefs.current[tab]
      if (!el || !link) return
      // Measure against the STRIP, not offsetLeft: app.css makes each
      // li.nav-item `position: relative`, so the link's offsetLeft is 0 (its
      // offset parent is the li) and the target came out negative → clamped
      // to 0 → no move at all (user's recording, 2026-09-22).
      const strip = el.getBoundingClientRect()
      const box = link.getBoundingClientRect()
      const tabCentre = el.scrollLeft + (box.left - strip.left) + box.width / 2
      glideTo(tabCentre - el.clientWidth / 2)
    })
  }

  // the arrows' 300px jump, eased the same way the reference's CSS eases it
  const scrollTabs = (dir) => {
    if (tabsRef.current) glideTo(tabsRef.current.scrollLeft + dir * ARROW_STEP_PX)
  }

  return (
    <>
      <div className="casino-tabs-admin p-2">
        <div className="casino-tabs-menu w-100">
          <a href="javascript:void(0)" className="arrow-tabs arrow-left" onClick={() => scrollTabs(-1)}>
            <i className="mdi mdi-chevron-left"></i>
          </a>
          {' '}
          <ul className="nav nav-tabs nav-tabs-custom" ref={tabsRef}>
            {tabs.map((tab) => (
              <li key={tab} className="nav-item">
                <a
                  href="javascript:void(0)"
                  ref={(el) => { linkRefs.current[tab] = el }}
                  className={`nav-link${activeTab === tab ? ' active' : ''}`}
                  onClick={() => pickTab(tab)}
                >
                  {'\n                ' + tab + '\n              '}
                </a>
              </li>
            ))}
          </ul>
          {' '}
          <a href="javascript:void(0)" className="arrow-tabs arrow-right" onClick={() => scrollTabs(1)}>
            <i className="mdi mdi-chevron-right"></i>
          </a>
        </div>
      </div>
      {' '}
      <div className="casino-banners" key={listKey}>
        {!blank && items.map((item) => (
          <div className="casino-banner-item" key={itemKey(item)}>
            {renderItem(item)}
          </div>
        ))}
      </div>
    </>
  )
}
